import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchMyShifts, MyShiftsFilter } from "../../redux/MyShiftsReducer/myShiftsSlice";
import ShiftPageContainer from "../../components/shift-page-container/ShiftPageContainer";
import { useIsGigWorker } from "../../hooks/useIsGigWorker";
import GigWorkerShiftsCalendar from "./GigWorkerShiftsCalendar";
import { mapGigGroupsToShiftCards } from "../../utils/gigShiftUtils";
import { sortShiftsChronologically } from "../../utils/shiftUtils";

type StatusFilter = "all" | "APP" | "DEN" | "PND";

const filterMap: Record<number, MyShiftsFilter> = {
    0: "past",
    1: "upcoming",
    2: "all",
};

const DEFAULT_TAB_INDEX = 1;
const DEFAULT_STATUS_FILTER: StatusFilter = "all";

const statusOptions = [
    { label: "All", value: "all" },
    { label: "Approved", value: "APP" },
    { label: "Cancelled", value: "CAN" },
    { label: "Denied", value: "DEN" },
    { label: "Pending", value: "PND" },
];

const MyShiftsPage: React.FC = () => {
    const isGigWorker = useIsGigWorker();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.currentUser);
    const { shifts, gigShifts, loading } = useAppSelector((state) => state.myShifts);

    const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB_INDEX);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>(DEFAULT_STATUS_FILTER);
    const [myShiftsTabs] = useState([
        { label: "Past", content: "" },
        { label: "Upcoming", content: "" },
        { label: "All", content: "" },
    ]);

    const loadShifts = useCallback(
        (tabIndex: number, status: StatusFilter) => {
            if (user?.siteCd && user?.userId) {
                const filter = filterMap[tabIndex];
                const statuses = status === "all" ? [] : [status];
                dispatch(
                    fetchMyShifts({
                        siteCd: user.siteCd,
                        userId: user.userId,
                        filter,
                        statuses,
                    })
                );
            }
        },
        [dispatch, user?.siteCd, user?.userId]
    );

    const handleTabChange = (tabIndex: number) => {
        setSelectedTab(tabIndex);
        loadShifts(tabIndex, statusFilter);
    };

    const handleStatusChange = (value: string) => {
        const status = value as StatusFilter;
        setStatusFilter(status);
        loadShifts(selectedTab, status);
    };

    useEffect(() => {
        loadShifts(DEFAULT_TAB_INDEX, DEFAULT_STATUS_FILTER);
    }, [loadShifts]);

    // Filter GIG groups client-side to match the currently selected tab/status.
    const filteredGigShifts = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tabFilter = filterMap[selectedTab];
        return gigShifts.filter((g) => {
            if (statusFilter !== "all" && g.overallStatus !== statusFilter) return false;
            if (tabFilter === "all" || !g.lastDate) return true;
            const last = new Date(g.lastDate + "T00:00:00");
            if (tabFilter === "past") return last < today;
            if (tabFilter === "upcoming") return last >= today;
            return true;
        });
    }, [gigShifts, selectedTab, statusFilter]);

    const mixedShifts = useMemo(
        () => sortShiftsChronologically([
            ...shifts.map((shift) => ({
                ...shift,
                location: shift.location || user?.siteCd || '',
            })),
            ...mapGigGroupsToShiftCards(filteredGigShifts, user?.siteCd || ''),
        ]),
        [shifts, filteredGigShifts, user?.siteCd]
    );

    if (isGigWorker) {
        return <GigWorkerShiftsCalendar />;
    }

    return (
        <>
            <ShiftPageContainer
                title="My Shifts"
                subtitle="Browse and view shifts you have claimed."
                tabs={myShiftsTabs}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
                shifts={mixedShifts}
                loading={loading}
                noShiftsMessage="No claimed shifts"
                filterLabel="Status"
                filterOptions={statusOptions}
                filterValue={statusFilter}
                onFilterChange={handleStatusChange}
                readOnly={true}
            />
        </>
    );
};

export default MyShiftsPage;